import { CardDirection, CardIconType, CardSize } from '../card';
import { ContentCriterionType, ContentValidationCriterion, ProductError, ProductErrorType } from '../../models';

export const FLIP_CARD_TITLE_LIMIT = 10;

export class FlipCardData {
  cardRows: FlipCard[][];
  cardDirection: CardDirection;
  cardTransition: FlipCardTransition;
  cardSize: CardSize;
  validationCriteria: ContentValidationCriterion[];
  title?: string;

  constructor(cardData?: FlipCardData) {
    this.cardRows = (cardData?.cardRows || []).map(row => (row || []).map(c => new FlipCard(c)));
    this.cardDirection = cardData?.cardDirection || CardDirection.REGULAR;
    this.cardTransition = cardData?.cardTransition || FlipCardTransition.HORIZONTAL;
    this.cardSize = cardData?.cardSize || CardSize.M;
    this.validationCriteria = cardData
      ? (cardData?.validationCriteria || []).map(vc => new ContentValidationCriterion(vc))
      : [new ContentValidationCriterion({ type: ContentCriterionType.CLICK_ALL })]; // as default value only for initial creation
    this.title = cardData?.title || null;
  }

  static validate(cardData: FlipCardData, payloadData: any): ProductError[] {
    if (!cardData || !cardData.cardRows || !cardData.cardRows.length) {
      return [{ type: ProductErrorType.MISSING, property: 'cardRows', payload: { ...payloadData } }];
    }
    const errors = [];
    (cardData.cardRows || []).forEach((cardRow, cardRowIndex) => {
      if (Array.isArray(cardRow)) {
        (cardRow || []).forEach((card, cardIndex) => {
          errors.push(FlipCard.validate(card, { ...payloadData, cardRowIndex, cardIndex }, cardData.cardDirection));
        });
      } else {
        errors.push({
          type: ProductErrorType.BAD_FORMAT,
          property: 'cardRows',
          payload: { ...payloadData, cardRowIndex },
        });
      }
    });
    return errors.reduce((flattenedArray: any[], subArray) => {
      flattenedArray.push(...subArray);
      return flattenedArray;
    }, []);
  }
}

export class FlipCard {
  slug: string;
  firstHeader?: string;
  firstIconType?: CardIconType;
  firstIcon?: string;
  secondHeader?: string;
  secondIconType?: CardIconType;
  secondIcon?: string;

  constructor(card?: FlipCard) {
    this.slug = card ? card.slug : null;
    this.firstHeader = card ? card.firstHeader : null;
    this.firstIconType = card ? card.firstIconType : null;
    this.firstIcon = card ? card.firstIcon : null;
    this.secondIcon = card ? card.secondIcon : null;
    this.secondHeader = card ? card.secondHeader : null;
    this.secondIconType = card ? card.secondIconType : null;
  }

  static validate(card: FlipCard, payloadData: any, cardDirection: CardDirection): ProductError[] {
    let primitivesError: ProductError[];
    switch (cardDirection) {
      case CardDirection.REGULAR:
      case CardDirection.REVERSE:
        primitivesError = ProductError.validatePrimitiveTypes(card, [
          'slug',
          'firstHeader',
          'firstIconType',
          'firstIcon',
          'secondHeader',
          'secondIconType',
          'secondIcon',
        ]).map(error => ({ ...error, payload: { ...payloadData } }));
        break;
      case CardDirection.ONLY_ICON:
        primitivesError = ProductError.validatePrimitiveTypes(card, [
          'slug',
          'firstIconType',
          'firstIcon',
          'secondHeader',
        ]).map(error => ({ ...error, payload: { ...payloadData } }));
        break;
      case CardDirection.ONLY_TEXT:
        primitivesError = ProductError.validatePrimitiveTypes(card, ['slug', 'firstHeader', 'secondHeader']).map(
          error => ({ ...error, payload: { ...payloadData } }),
        );
        break;
    }
    return primitivesError;
  }
}

export class FlipCardLocal {
  card: FlipCard;
  recto = true;

  constructor(card?: FlipCard) {
    this.card = card ? card : new FlipCard();
  }

  toggle(): boolean {
    this.recto = !this.recto;
    return this.recto;
  }
}

export class FlipCardProgression {
  flipCardProgressionList: FlipCardProgressionItem[];

  constructor(progression?: FlipCardProgression) {
    this.flipCardProgressionList = (progression?.flipCardProgressionList || []) //
      .map(v => new FlipCardProgressionItem(v));
  }
}

export class FlipCardProgressionItem {
  cardSlug: string;

  constructor(cardProgression: FlipCardProgressionItem) {
    this.cardSlug = cardProgression.cardSlug;
  }
}

export enum FlipCardTransition {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical',
}

export interface FlipCardTransitionObject {
  recto: string;
  verso: string;
}
